import { Button, Flex, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoginUser } from '../../../api/hooks/useUserQuery';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { SignInStageEnum } from '../../../enums/SignInStage';
import { UsersStatusEnum } from '../../../enums/UserStatusEnum';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { validationRules } from '../../../helpers/formHelper';
import SignInHeader from './SignInHeader';
import { useSetUserAndToken } from '../helpers/signinHelper';
import { useUserContext } from '../../../contexts/UserContext';
import { useOTPContext } from '../../../contexts/OtpContext';
import { customizeRequiredMark } from '../../../helpers/formHelper';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { useNavigate } from 'react-router-dom';

interface SignInSectionProps {
  onChangeStage: Function;
}

interface UserLoginParams {
  email: string;
  password: string;
  deviceToken: string;
}

const SignInSection = ({ onChangeStage }: SignInSectionProps) => {
  const { t } = useTranslation(['signIn', 'common', 'form']);
  const navigate = useNavigate();
  const userLogin = useLoginUser();
  const [signInForm] = Form.useForm();
  const { errorNotification, successNotification } = useNotificationContext();
  const { setUserAndToken } = useSetUserAndToken();
  const { getUserInfo } = useUserContext();
  const { handleSetEmail, handleSetSigninOtpId, handleSetOtpCreateTime } = useOTPContext();
  const onFinish = (values: { email: string; password: string }) => {
    const { email, password } = values;
    const deviceToken = getUserInfo('deviceToken');
    userLogin.mutate(
      {
        email,
        password,
        deviceToken,
      } as UserLoginParams,
      {
        onSuccess: async (data: any) => {
          const isOtpEnabled = ENVIRONMENT_CONFIG.OTPENABLED;
          const otpStatus = data?.data?.data?.otpStatus;
          const otpId = data?.data?.data?.otpId;
          const otpCreateTime = data?.data?.data?.createTime;
          const email = values.email;
          handleSetEmail(email);
          if (otpStatus != undefined && otpStatus && isOtpEnabled) {
            handleSetSigninOtpId(otpId);
            handleSetOtpCreateTime(otpCreateTime);
            onChangeStage(SignInStageEnum.OTP);
            return;
          }

          if (data?.data?.data?.status === UsersStatusEnum.PASSWORD_CHANGE_REQUIRED) {
            onChangeStage(SignInStageEnum.CHANGE_PASSWORD);
            return;
          }

          if (data?.data?.data?.status === UsersStatusEnum.EXPIRED) {
            errorNotification(t('passwordExpired'), { duration: 5 });
            onChangeStage(SignInStageEnum.CHANGE_PASSWORD);
            return;
          }

          const dataObject = data.data;
          if (!dataObject.deviceToken) {
            successNotification(t('successLoginWithDeviceToken'));
          }
          await setUserAndToken(
            dataObject.user,
            dataObject.token,
            dataObject.allowPermission,
            dataObject.deviceToken
          );
        },
        onError(response) {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      }
    );
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };
  return (
    <Flex className="h-full w-full" justify="center" align="center" vertical gap={8}>
      <SignInHeader></SignInHeader>
      <Form
        form={signInForm}
        name="sign-in"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        className="w-full max-w-[400px]"
        validateTrigger={false}
        requiredMark={customizeRequiredMark}
      >
        <Form.Item
          label={t('email')}
          name="email"
          rules={[
            { required: true, message: t('emailRequired') },
            { type: 'email', message: t('invalidEmail') },
          ]}
          className="mb-4"
          normalize={(value) => value.trim()}
        >
          <Input
            autoCapitalize="none"
            onChange={() => {
              signInForm.validateFields(['email']);
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('password')}
          name="password"
          rules={[
            { required: true, message: '' },
            { validator: validationRules(t).PASSWORD_REQUIRED_ONLY },
          ]}
        >
          <Input.Password
            onChange={() => {
              signInForm.validateFields(['password']);
            }}
          />
        </Form.Item>

        <Form.Item>
          <p
            className="cursor-pointer text-right text-sm font-medium hover:underline"
            onClick={() => onChangeStage(SignInStageEnum.FORGET_PASSWORD, {})}
          >
            {t('forgetYourPassword')}
          </p>
        </Form.Item>

        <Form.Item>
          <Button
            className="btn-primary"
            type="primary"
            htmlType="submit"
            block
            loading={userLogin.isPending}
          >
            {t('signIn')}
          </Button>
        </Form.Item>
        <Form.Item>
          <p className="text-center text-sm font-medium">
            {t('dontHaveAccount')}{' '}
            <span
              className="cursor-pointer font-semibold text-primary hover:underline"
              onClick={() => navigate('/register', { replace: true })}
            >
              {t('signup')}
            </span>
          </p>
        </Form.Item>
      </Form>
    </Flex>
  );
};

export default SignInSection;
